import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PhaseListForms } from "../types";
import {
  ModalBaseStyles,
  ModalHeaderTitleStyles,
} from "../../Common/styles/modal";
import { useState } from "react";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { togglePhaseLoader } from "../../../Redux/reducers/responseSlice";
import http from "../../../utils/http";
import { useParams } from "react-router-dom";

type Props = {
  show: boolean;
  onClose: () => void;
  form: PhaseListForms;
};

const SignModal: React.FC<Props> = ({ form, onClose, show }) => {
  const dispatch = useAppDispatch();
  const { id: studyId, participantId } = useParams();
  const repeatedAttemptId = useAppSelector(
    (state) => state.response.repeatedAttemptId
  );
  const [reason, setReason] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [unlock, setUnlock] = useState(false);
  const [lock, setLock] = useState(false);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReason(e.target.value);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleContinue = async () => {
    try {
      if (form.isSigned && !reason) {
        toastMessage("warning", "Reason cannot be empty!");
        return;
      }
      setLoading(true);
      await http.post(
        `/study/${studyId}/responses/${form?.id}/sign?participantId=${participantId}`,
        {
          sign: form.isSigned ? false : true,
          reason: form.isSigned ? reason : undefined,
          unlock: form.isSigned ? unlock : false,
          repeatedAttemptId: repeatedAttemptId || undefined,
          lock: !form.isSigned ? lock : false,
        }
      );

      toastMessage(
        "success",
        form.isSigned
          ? "Form unlocked successfully"
          : "Form locked successfully"
      );
      onClose();
      dispatch(togglePhaseLoader());
    } catch (err) {
      errorToastMessage(err as Error);
      setLoading(false);
    }
  };

  return (
    <Modal open={show}>
      <Box
        sx={{ ...ModalBaseStyles, height: "fit-content", minHeight: "20vh" }}
      >
        <Typography sx={ModalHeaderTitleStyles} mb={1}>
          {form.isSigned ? "Unsign Form" : "Sign Form"}
        </Typography>

        <Divider />
        {form.isSigned ? (
          <Typography mt={2} variant="body1">
            Are you sure want to remove the signature from this form. This event
            will be recorded in audit trail.
          </Typography>
        ) : (
          <Typography mt={2} variant="body1">
            By signing this form, you declare the following: The Information
            provided in this form is true and accurate. This event will be
            recorded in audit trail.
          </Typography>
        )}

        {form.isSigned && (
          <Stack gap={1} marginY={2}>
            <Typography variant="body1" fontWeight="regular">
              Reason for unsigning the form
            </Typography>
            <TextField
              name="reason"
              fullWidth
              placeholder="Enter reason"
              value={reason}
              onChange={changeHandler}
            />
          </Stack>
        )}

        {form.isSigned && form.locked && (
          <FormControlLabel
            label={
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                color="text.primary"
              >
                Unlock Form
              </Typography>
            }
            control={
              <Checkbox
                checked={unlock}
                onChange={(event) => {
                  setUnlock(event.target.checked);
                }}
              />
            }
          />
        )}

        {!form.isSigned && !form.locked && (
          <FormControlLabel
            label={
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                color="text.primary"
              >
                Lock Form
              </Typography>
            }
            control={
              <Checkbox
                checked={lock}
                onChange={(event) => {
                  setLock(event.target.checked);
                }}
              />
            }
          />
        )}

        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"flex-end"}
          sx={{ marginTop: "auto" }}
          gap={1.5}
        >
          {isLoading ? (
            <CircularProgress color="inherit" size={18} />
          ) : (
            <>
              <Button variant="outlined" onClick={handleCancel}>
                No
              </Button>

              <Button variant="contained" onClick={handleContinue}>
                Yes
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default SignModal;
